import { useChangeEnrollmentsStatus } from '@actions';
import TextArea from '@components/V4/Inputs/Textarea';
import Select from '@components/V4/Select/Select';
import { zodResolver } from '@hookform/resolvers/zod';
import { ENROLLMENT_STATUS_OPTIONS, EnrollmentStatusTypes } from '@shared/common';
import { changeEnrollmentStatusValidation } from '@shared/validations';
import useToast from 'apps/agora/src/hooks/useToast';
import React, { useEffect, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { z } from 'zod';
import SessionModalFormWrapper from '../../DetailsModalFormWrapper';

interface EnrollmentsListChangeStatusFormProps {
  enrollmentIds: string[];
  onCancel: () => void;
}

const formDataSchema = changeEnrollmentStatusValidation;

type FormData = z.infer<typeof formDataSchema>;

const EnrollmentsListChangeStatusForm = (props: EnrollmentsListChangeStatusFormProps) => {
  const { enrollmentIds, onCancel } = props;

  const toast = useToast();

  const [initialReason, setInitialReason] = useState<string>('');

  const { handleSubmit, trigger, setValue, control, getValues, register } = useForm<FormData>({
    resolver: zodResolver(formDataSchema),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
  });

  const { field: statusField, fieldState: statusFieldState } = useController({
    name: 'newStatus',
    control,
  });

  const { mutate: changeEnrollmentsStatus, isLoading } = useChangeEnrollmentsStatus({
    onSuccess: async () => {
      toast.success('Successfully changed enrollments status.');
      onCancel();
    },
    onError: () => {
      toast.error('Enrollments status could not be changed.');
    },
  });

  const onSubmit = async (data: FormData) => {
    const isValid = await trigger();
    if (!isValid) return;

    changeEnrollmentsStatus({
      enrollmentIds,
      newStatus: data.newStatus as EnrollmentStatusTypes,
      ...(data.reason !== initialReason &&
        data.reason && {
          reason: data.reason,
        }),
    });
  };

  const touchAllFields = (fields: Record<keyof FormData, string | EnrollmentStatusTypes>) => {
    Object.keys(fields).forEach((key) => {
      setValue(key as keyof FormData, getValues()[key as keyof FormData], {
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  const submitHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    const formData = getValues();

    touchAllFields(formData);

    handleSubmit(onSubmit)(e);
  };

  return (
    <SessionModalFormWrapper
      title={'Enrollment Status'}
      isLoading={isLoading}
      onCancel={onCancel}
      submitHandler={submitHandler}
    >
      <Select
        value={statusField.value}
        onSelect={(value) => statusField.onChange(value)}
        options={ENROLLMENT_STATUS_OPTIONS}
        onBlur={statusField.onBlur}
        label="Status"
        isValid={!statusFieldState.error}
        isTouched={statusFieldState.isTouched}
        errorText={statusFieldState.error?.message}
        isRequired
      />

      <TextArea id="reason" label="Reason" className="h-40" {...register('reason')} />
    </SessionModalFormWrapper>
  );
};

export default EnrollmentsListChangeStatusForm;
